import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Banknote, AlertTriangle, Calendar, CalendarClock, Repeat, ChevronRight } from 'lucide-react';
import api from '@/lib/api';

const fmtMoney = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BANK_TONES = {
  'Operating Account': 'text-sky-300',
  'Vendor': 'text-amber-300',
  'Savings': 'text-emerald-300',
  'Unassigned': 'text-zinc-300',
};

const BANK_DOT_BG = {
  'Operating Account': 'bg-sky-400',
  'Vendor': 'bg-amber-400',
  'Savings': 'bg-emerald-400',
  'Unassigned': 'bg-zinc-400',
};

// Other components can fire `window.dispatchEvent(new Event('liabilities:refresh'))`
// after creating / editing / deleting / paying an AP to make every mounted
// widget refetch without a full page reload.
export const LIABILITIES_REFRESH_EVENT = 'liabilities:refresh';

/**
 * Liabilities by Bank Account widget - three layouts:
 *  - variant="dashboard": single compact card, total + per-bank rows, click → AP tab
 *  - variant="overview":  card grid (one per bank) with urgency split
 *  - variant="ap":        condensed banner above the AP table (urgency split, smaller)
 */
export function LiabilitiesByBankWidget({ variant = 'overview' }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchData = useCallback(() => {
    api.get('/accounts-payable/liabilities-by-bank')
      .then(r => { setData(r.data); setError(false); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  useEffect(() => {
    fetchData();
    const onRefresh = () => fetchData();
    window.addEventListener(LIABILITIES_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(LIABILITIES_REFRESH_EVENT, onRefresh);
  }, [fetchData]);

  const goToAP = (extra = {}) => {
    const params = new URLSearchParams({ tab: 'payable', ...extra });
    navigate(`/accounting?${params.toString()}`);
  };

  if (loading) {
    return (
      <Card className="border-border bg-card shadow-none" data-testid="liabilities-widget-loading">
        <CardContent className="p-4 text-xs text-muted-foreground">Loading liabilities…</CardContent>
      </Card>
    );
  }
  if (error || !data) {
    return null;
  }

  // Only render banks that have actual liability OR a projected recurring instance.
  // Hides empty 0-record chips entirely per UX decision (Phase 66.1).
  const banks = data.bank_order
    .map(b => ({ name: b, ...(data.banks?.[b] || {}) }))
    .filter(b => (b.total_count || 0) > 0 || (b.projected_recurring?.count || 0) > 0);

  if (variant === 'dashboard') {
    return (
      <Card
        className="border-white/10 bg-card shadow-none hover:bg-secondary/10 transition-colors cursor-pointer"
        onClick={() => goToAP()}
        data-testid="liabilities-widget-dashboard"
      >
        <CardHeader className="pb-2 border-b border-white/[0.08]">
          <CardTitle className="text-sm font-medium flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 min-w-0 flex-1">
              <Banknote className="h-3.5 w-3.5 text-violet-400 shrink-0" />
              <span className="truncate">Liabilities by Bank Account</span>
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2.5 pt-3">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-2xl font-medium font-mono tabular-nums" data-testid="liabilities-grand-total">{fmtMoney(data.grand.total)}</span>
            <span className="text-xs uppercase tracking-wider text-muted-foreground">total outstanding · {data.grand.total_count} record{data.grand.total_count === 1 ? '' : 's'}</span>
          </div>
          {data.grand.overdue.count > 0 && (
            <div className="flex items-center gap-1.5 text-xs">
              <AlertTriangle className="h-3 w-3 text-rose-400" />
              <span className="text-rose-400 font-medium">{fmtMoney(data.grand.overdue.amount)} overdue</span>
              <span className="text-muted-foreground">· {data.grand.overdue.count} record{data.grand.overdue.count === 1 ? '' : 's'}</span>
            </div>
          )}
          {data.grand.total === 0 && data.grand.projected_recurring.count === 0 && (
            <p className="text-xs text-muted-foreground italic">No outstanding liabilities. All clear.</p>
          )}
          {banks.length > 0 && (
            <div className="space-y-1.5 pt-1.5 border-t border-border">
              {banks.map(b => (
                <div
                  key={b.name}
                  className="flex items-center justify-between gap-2 text-xs hover:bg-secondary/20 rounded px-1.5 -mx-1.5 py-1"
                  onClick={(e) => { e.stopPropagation(); goToAP({ bank: b.name }); }}
                  data-testid={`liabilities-dashboard-row-${b.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="flex items-center gap-1.5">
                    <span className={`h-1.5 w-1.5 rounded-full ${BANK_DOT_BG[b.name]}`} />
                    <span className={`${BANK_TONES[b.name]} font-medium`}>{b.name}</span>
                  </span>
                  <span className="tabular-nums">
                    {b.overdue.count > 0 && <span className="text-rose-400 mr-1.5">{fmtMoney(b.overdue.amount)}</span>}
                    <span className="text-foreground">{fmtMoney(b.total)}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // overview + ap variants share a similar full grid layout - `compact` makes paddings tighter for AP banner
  const compact = variant === 'ap';
  return (
    <Card className="border-border bg-card shadow-none" data-testid={`liabilities-widget-${variant}`}>
      <CardHeader className={compact ? 'pb-2 pt-3 px-4' : 'pb-3'}>
        <CardTitle className="text-sm font-heading flex items-center justify-between gap-2">
          <span className="flex items-center gap-2"><Banknote className="h-4 w-4 text-violet-400" /> Liabilities by Bank Account</span>
          <span className="text-xs font-normal text-muted-foreground">Outstanding {fmtMoney(data.grand.total)} · {data.grand.total_count} record{data.grand.total_count === 1 ? '' : 's'}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className={compact ? 'px-4 pb-4 pt-0' : 'pt-0'}>
        {banks.length === 0 ? (
          <p className="text-xs text-muted-foreground italic py-2">No outstanding liabilities across all bank accounts.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {banks.map(b => (
              <div
                key={b.name}
                className="border border-border rounded-md p-3 bg-card hover:bg-secondary/10 transition-colors"
                data-testid={`liabilities-bank-card-${b.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <button
                  type="button"
                  onClick={() => goToAP({ bank: b.name })}
                  className="w-full text-left group"
                  data-testid={`liabilities-bank-card-header-${b.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="flex items-center gap-1.5 text-xs font-semibold">
                      <span className={`h-2 w-2 rounded-full ${BANK_DOT_BG[b.name]}`} />
                      <span className={BANK_TONES[b.name]}>{b.name}</span>
                    </span>
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <p className="text-xl font-heading font-bold tabular-nums mb-2 text-foreground">{fmtMoney(b.total)}</p>
                </button>
                <div className="space-y-1 text-xs">
                  <UrgencyRow
                    icon={<AlertTriangle className="h-3 w-3 text-rose-400" />}
                    label="Overdue"
                    bucket={b.overdue}
                    tone="text-rose-300"
                    onClick={b.overdue.count > 0 ? () => goToAP({ bank: b.name, urgency: 'overdue' }) : null}
                    testId={`liabilities-row-${b.name.toLowerCase().replace(/\s+/g, '-')}-overdue`}
                  />
                  <UrgencyRow
                    icon={<CalendarClock className="h-3 w-3 text-amber-400" />}
                    label="Due this week"
                    bucket={b.due_this_week}
                    tone="text-amber-300"
                    onClick={b.due_this_week.count > 0 ? () => goToAP({ bank: b.name, urgency: 'due_week' }) : null}
                    testId={`liabilities-row-${b.name.toLowerCase().replace(/\s+/g, '-')}-due-week`}
                  />
                  <UrgencyRow
                    icon={<Calendar className="h-3 w-3 text-sky-400" />}
                    label="Due later"
                    bucket={b.due_later}
                    tone="text-sky-300"
                    onClick={b.due_later.count > 0 ? () => goToAP({ bank: b.name, urgency: 'due_later' }) : null}
                    testId={`liabilities-row-${b.name.toLowerCase().replace(/\s+/g, '-')}-due-later`}
                  />
                  <UrgencyRow
                    icon={<Repeat className="h-3 w-3 text-violet-400" />}
                    label="Next recurring"
                    bucket={b.projected_recurring}
                    tone="text-violet-300"
                    projected
                    onClick={b.projected_recurring.count > 0 ? () => goToAP({ bank: b.name, urgency: 'recurring' }) : null}
                    testId={`liabilities-row-${b.name.toLowerCase().replace(/\s+/g, '-')}-recurring`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-2.5 italic">
          Click a bank name to filter the AP table by that bank, or click an urgency row to also filter by that bucket.
          {data.grand.projected_recurring?.count > 0 && (
            <> · &quot;Next recurring&quot; projects {data.grand.projected_recurring.count} upcoming instance{data.grand.projected_recurring.count === 1 ? '' : 's'}, not folded into the outstanding total.</>
          )}
        </p>
      </CardContent>
    </Card>
  );
}

function UrgencyRow({ icon, label, bucket, tone, projected, onClick, testId }) {
  const isZero = !bucket?.count;
  const clickable = !!onClick && !isZero;
  return (
    <div
      className={`flex items-center justify-between rounded px-1 -mx-1 transition-colors ${clickable ? 'cursor-pointer hover:bg-secondary/30' : ''}`}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      data-testid={testId}
    >
      <span className="flex items-center gap-1.5">
        {icon}
        <span className={isZero ? 'text-muted-foreground' : tone}>{label}</span>
      </span>
      <span className="tabular-nums">
        <span className={isZero ? 'text-muted-foreground' : tone}>
          {projected && bucket.amount > 0 ? '~' : ''}{fmtMoney(bucket.amount)}
        </span>
        {bucket.count > 0 && <Badge variant="outline" className="text-xs ml-1.5 px-1.5 py-0 h-4">{bucket.count}</Badge>}
      </span>
    </div>
  );
}
